import { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';

interface Role {
  id: string;
  name: string;
}

interface RoleMultiSelectProps {
  roles: Role[];
  selectedRoles: string[];
  onChange: (selectedRoles: string[]) => void;
  loading?: boolean;
  label?: string;
  placeholder?: string;
}

export function RoleMultiSelect({
  roles,
  selectedRoles,
  onChange,
  loading = false,
  label = 'Roles con Visibilidad',
  placeholder = 'Selecciona los roles que pueden ver este formulario...',
}: RoleMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleRole = (roleName: string) => {
    if (selectedRoles.includes(roleName)) {
      onChange(selectedRoles.filter((r) => r !== roleName));
    } else {
      onChange([...selectedRoles, roleName]);
    }
  };

  const selectedRoleNames = roles.filter((r) => selectedRoles.includes(r.name));

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-foreground mb-1">
        {label}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground flex items-center justify-between"
          disabled={loading}
        >
          <span className="truncate">
            {selectedRoleNames.length === 0
              ? placeholder
              : `${selectedRoleNames.length} rol(es) seleccionado(s)`}
          </span>
          <Icon
            icon={isOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'}
            className="w-4 h-4 text-muted-foreground ml-2 flex-shrink-0"
          />
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-auto">
            {loading && (
              <div className="p-3 text-sm text-muted-foreground text-center">
                Cargando roles...
              </div>
            )}
            {!loading && roles.length === 0 && (
              <div className="p-3 text-sm text-muted-foreground text-center">
                No hay roles disponibles
              </div>
            )}
            {!loading && roles.length > 0 && (
              <div className="p-1">
                {roles.map((role) => {
                  const isSelected = selectedRoles.includes(role.name);
                  return (
                    <label
                      key={role.id}
                      className="flex items-center px-3 py-2 hover:bg-muted cursor-pointer rounded-sm"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRole(role.name)}
                        className="mr-2 rounded border-input text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-foreground">{role.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      {selectedRoleNames.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedRoleNames.map((role) => (
            <span
              key={role.id}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary"
            >
              {role.name}
              <button
                type="button"
                onClick={() => toggleRole(role.name)}
                className="ml-1 hover:text-primary/80"
              >
                <Icon icon="mdi:close" className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-1">
        Los roles seleccionados podrán ver las respuestas y exportar a Excel de este formulario.
      </p>
    </div>
  );
}
